import type { MouseEvent, ReactNode, RefObject } from "react";
import { Eye, Loader2, Plus, type LucideIcon } from "lucide-react";

import { AppLink } from "@/components/app-link";
import { EmptyState } from "@/components/empty-state";
import { CountBadge } from "@/components/semantic-badges";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useCanEdit } from "@/lib/capabilities";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { cn, formatDate, formatRelativeTime } from "@/lib/utils";

import { PageHeader } from "./page-header";

export interface JobListColumn<Row> {
  id: string;
  header: string;
  cell: (row: Row) => ReactNode;
  /**
   * `number` right-aligns with tabular figures (counts); `muted` is for secondary text (people,
   * dates, source names). Status and badge cells use `default`.
   */
  variant?: "default" | "number" | "muted";
  /** Width or other classes applied to both the header and the cells. */
  className?: string;
}

/** Where a row opens. Used by the View button and by clicking anywhere else on the row. */
export interface JobListRowLink {
  to: string;
  params: Record<string, string>;
  /** Tab title in workspace mode; also the link's tooltip. */
  title: string;
}

export interface JobListPageProps<Row> {
  /** Page title, e.g. "SpecPilot". */
  title: string;
  /** The primary "New …" button in the header and the empty state. Hidden for viewers who can't edit. */
  newAction?: { label: string; onClick: () => void };
  /** Extra header buttons placed before the primary action. Hidden for viewers who can't edit. */
  secondaryActions?: ReactNode;
  emptyState: { icon: LucideIcon; title: string; description: string };
  /** Shows the skeleton table while the project or first page loads. */
  isLoading: boolean;
  rows: readonly Row[];
  getRowKey: (row: Row) => string;
  /** Used for the View button, row clicks, and accessible names ("Actions for …"). */
  getRowName: (row: Row) => string;
  getRowLink: (row: Row) => JobListRowLink;
  /** Name cell content, usually an `InlineEditNameCell`. Defaults to the plain name. */
  renderName?: (row: Row) => ReactNode;
  /** Columns between Name and the row actions, e.g. Status, Kind, counts, `jobListDateColumn`. */
  columns: JobListColumn<Row>[];
  /** Row actions cell content, usually a `RowActionsMenu`. */
  renderActions?: (row: Row) => ReactNode;
  /** Extra controls next to the View button, e.g. a revision-history expander. */
  renderViewExtras?: (row: Row) => ReactNode;
  getRowClassName?: (row: Row) => string | undefined;
  /** True while more pages exist, so the header count reads "40+". */
  hasMore?: boolean;
  /** Rendered under the table, e.g. an infinite-scroll sentinel. */
  footer?: ReactNode;
  /** Page-level dialogs. */
  children?: ReactNode;
}

const VIEW_COLUMN_CLASSES = "w-[70px]";
const ACTIONS_COLUMN_CLASSES = "w-[70px]";
const SKELETON_ROW_COUNT = 5;

/** Elements inside a row that handle their own clicks, so the row does not also navigate. */
const INTERACTIVE_SELECTOR =
  'a, button, input, select, textarea, label, [role="button"], [role="combobox"], [role="menuitem"], [role="option"], [contenteditable="true"]';

function columnClasses(variant: JobListColumn<unknown>["variant"]) {
  switch (variant) {
    case "number":
      return "text-right tabular-nums";
    case "muted":
      return "text-muted-foreground";
    default:
      return undefined;
  }
}

/** Relative time with the full date on hover, the standard date cell for list pages. */
export function RelativeDate({ timestamp }: { timestamp: number }) {
  return (
    <time dateTime={new Date(timestamp).toISOString()} title={formatDate(timestamp)}>
      {formatRelativeTime(timestamp)}
    </time>
  );
}

/** A muted relative-date column, e.g. `jobListDateColumn("Created", (job) => job.createdAt)`. */
export function jobListDateColumn<Row>(
  header: string,
  getTimestamp: (row: Row) => number,
  id = header.toLowerCase().replace(/\s+/g, "-")
): JobListColumn<Row> {
  return {
    id,
    header,
    variant: "muted",
    cell: (row) => <RelativeDate timestamp={getTimestamp(row)} />,
  };
}

/**
 * The shared shape of the pilot job lists (SpecPilot, TakeoffPilot, FieldPilot, RevPilot): compact
 * header with a count and the "New …" action, skeleton while loading, empty state, then a table of
 * View · Name · route columns · row actions. Clicking a row's empty space opens it like View does.
 */
export function JobListPage<Row>({
  title,
  newAction,
  secondaryActions,
  emptyState,
  isLoading,
  rows,
  getRowKey,
  getRowName,
  getRowLink,
  renderName,
  columns,
  renderActions,
  renderViewExtras,
  getRowClassName,
  hasMore = false,
  footer,
  children,
}: JobListPageProps<Row>) {
  const canEdit = useCanEdit();
  const navigate = useAppNavigate();
  const showCount = !isLoading && rows.length > 0;

  const headerActions =
    canEdit && (secondaryActions || newAction) ? (
      <>
        {secondaryActions}
        {newAction ? (
          <Button size="sm" onClick={newAction.onClick}>
            <Plus />
            {newAction.label}
          </Button>
        ) : null}
      </>
    ) : undefined;

  const handleRowClick = (event: MouseEvent<HTMLTableRowElement>, row: Row) => {
    const target = event.target as Element;
    // React bubbles clicks from portals (menus, dialogs) through the row; ignore anything outside it.
    if (!event.currentTarget.contains(target)) return;
    const interactive = target.closest(INTERACTIVE_SELECTOR);
    if (interactive && event.currentTarget.contains(interactive)) return;
    if (window.getSelection()?.toString()) return;
    navigate(getRowLink(row));
  };

  let body: ReactNode;
  if (isLoading) {
    body = <JobListSkeleton columnCount={columns.length} />;
  } else if (rows.length === 0) {
    body = (
      <EmptyState
        icon={emptyState.icon}
        title={emptyState.title}
        description={emptyState.description}
        action={
          canEdit && newAction
            ? { label: newAction.label, icon: Plus, onClick: newAction.onClick }
            : undefined
        }
      />
    );
  } else {
    body = (
      <>
        <div className="rounded-lg bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className={VIEW_COLUMN_CLASSES}>
                  <span className="sr-only">View</span>
                </TableHead>
                <TableHead>Name</TableHead>
                {columns.map((column) => (
                  <TableHead
                    key={column.id}
                    className={cn(column.variant === "number" && "text-right", column.className)}
                  >
                    {column.header}
                  </TableHead>
                ))}
                <TableHead className={ACTIONS_COLUMN_CLASSES}>
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const name = getRowName(row);
                const link = getRowLink(row);
                return (
                  <TableRow
                    key={getRowKey(row)}
                    className={cn("group cursor-pointer", getRowClassName?.(row))}
                    onClick={(event) => handleRowClick(event, row)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <AppLink to={link.to} params={link.params} title={link.title}>
                          <IconButton label={`View ${name}`} icon={Eye} tooltip={false} />
                        </AppLink>
                        {renderViewExtras?.(row)}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">
                      {renderName ? renderName(row) : name}
                    </TableCell>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        className={cn(columnClasses(column.variant), column.className)}
                      >
                        {column.cell(row)}
                      </TableCell>
                    ))}
                    <TableCell className="text-right">{renderActions?.(row)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        {footer}
      </>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={title}
        meta={
          showCount ? (
            // CountBadge renders "{max}+" past max, so a partial list reads "40+".
            <CountBadge
              value={hasMore ? rows.length + 1 : rows.length}
              max={hasMore ? rows.length : 9999}
              label="total"
            />
          ) : undefined
        }
        actions={headerActions}
      />
      {body}
      {children}
    </div>
  );
}

function JobListSkeleton({ columnCount }: { columnCount: number }) {
  return (
    <div className="rounded-lg bg-card p-2" role="status" aria-label="Loading">
      <div className="space-y-1">
        {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
          <div key={index} className="flex h-12 items-center gap-4 px-2">
            <Skeleton className="size-8 shrink-0" />
            <Skeleton className="h-4 w-48" />
            {Array.from({ length: Math.min(columnCount, 4) }, (_, cellIndex) => (
              <Skeleton key={cellIndex} className="hidden h-4 w-20 sm:block" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Spinner shown under a paginated list while the next page loads. */
export function JobListLoadMore({
  sentinelRef,
  isLoadingMore,
}: {
  /** From `useInfiniteScroll`. */
  sentinelRef: RefObject<HTMLDivElement | null>;
  isLoadingMore: boolean;
}) {
  return (
    <div ref={sentinelRef} className="flex min-h-10 items-center justify-center">
      {isLoadingMore ? <Loader2 className="size-5 animate-spin text-muted-foreground" /> : null}
    </div>
  );
}
